/**
 * PROMPT INJECTION SANITIZATION
 *
 * Sanitizes user input before embedding in AI prompts to prevent
 * prompt injection attacks.
 *
 * Strategies:
 * - Detects common prompt injection patterns
 * - Strips control-like instructions from user text
 * - Wraps user input with clear delimiters
 */

import { Logger } from '@/core/utils';

const logger = Logger.getLogger('PromptSanitization');

// Patterns that indicate prompt injection attempts
const INJECTION_PATTERNS = [
  /ignore\s+(all\s+)?(previous|above|prior)\s+(instructions|prompts|rules)/i,
  /ignore\s+everything\s+(above|before)/i,
  /disregard\s+(all\s+)?(previous|prior|above)/i,
  /forget\s+(all\s+)?(previous|prior|your)\s+(instructions|rules|context)/i,
  /you\s+are\s+now\s+a/i,
  /act\s+as\s+(if\s+you\s+are|a|an)/i,
  /pretend\s+(you\s+are|to\s+be)/i,
  /new\s+instructions?:/i,
  /system\s*:\s*/i,
  /\[INST\]/i,
  /<\/?system>/i,
  /<<\s*SYS\s*>>/i,
  /ASSISTANT:/i,
  /HUMAN:/i,
];

/**
 * Sanitizes user input to prevent prompt injection.
 * Strips or flags suspicious patterns while preserving legitimate content.
 */
export function sanitizePromptInput(input: string): string {
  if (!input) return input;

  let sanitized = input;
  let injectionDetected = false;

  for (const pattern of INJECTION_PATTERNS) {
    if (pattern.test(sanitized)) {
      injectionDetected = true;
      sanitized = sanitized.replace(pattern, '[FILTERED]');
    }
  }

  if (injectionDetected) {
    logger.warn('Prompt injection pattern detected and sanitized');
  }

  return sanitized;
}

/**
 * Wraps user input with clear delimiters to help the AI model
 * distinguish between instructions and user content.
 */
export function wrapUserInput(input: string, label = 'USER_INPUT'): string {
  const sanitized = sanitizePromptInput(input);
  return `<${label}>\n${sanitized}\n</${label}>`;
}

/**
 * Validates that AI response doesn't contain suspicious patterns
 * that might indicate the model was successfully manipulated.
 */
export function validateAIResponse(response: string): { safe: boolean; reason?: string } {
  // Check for responses that might indicate prompt injection success
  const suspiciousPatterns = [
    /here\s+are\s+(the|my)\s+(previous|system)\s+instructions/i,
    /my\s+(system|initial)\s+prompt\s+is/i,
    /I\s+was\s+instructed\s+to/i,
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(response)) {
      logger.warn('AI response contains suspicious patterns - possible prompt leak');
      return { safe: false, reason: 'Response may contain leaked instructions' };
    }
  }

  return { safe: true };
}
